import { DataManager } from './DataManager';
import { GridDrawer } from './GridDrawer';
import type { CalendarInfo, LifeEvent } from './types';
import dayjs from 'dayjs';
import { showWarning } from './utils.ts';

const DEFAULT_EVENT_COLOR = '#2db5d5';

export class EventPopup {
  private readonly popup: HTMLElement;
  private readonly saveBtn: HTMLButtonElement;
  private readonly clearBtn: HTMLButtonElement;
  private readonly eventStartDateInput: HTMLInputElement;
  private readonly eventEndDateInput: HTMLInputElement;
  private readonly dateRangeDash: HTMLElement;
  private readonly eventRangeToggleBtn: HTMLButtonElement;
  private readonly colorInput: HTMLInputElement;
  private readonly descriptionInput: HTMLInputElement;

  private popupCurrentCalendarId: string = '';

  constructor(
    private readonly dataManager: DataManager,
    private readonly grid: GridDrawer,
    private readonly canvasContainer: HTMLElement,
  ) {
    this.popup = document.getElementById('eventEditPopup') as HTMLElement;
    this.saveBtn = document.getElementById('saveBtn') as HTMLButtonElement;
    this.clearBtn = document.getElementById('clearBtn') as HTMLButtonElement;
    this.eventStartDateInput = document.getElementById('eventStartDate') as HTMLInputElement;
    this.eventEndDateInput = document.getElementById('eventEndDate') as HTMLInputElement;
    this.dateRangeDash = document.getElementById('dateRangeDash') as HTMLElement;
    this.eventRangeToggleBtn = document.getElementById('eventRangeToggleBtn') as HTMLButtonElement;
    this.colorInput = document.getElementById('colorPicker') as HTMLInputElement;
    this.descriptionInput = document.getElementById('descriptionInput') as HTMLInputElement;

    this.colorInput.value = DEFAULT_EVENT_COLOR;

    this.eventRangeToggleBtn.addEventListener('click', () => {
      const isExpanded = !this.eventEndDateInput.classList.contains('d-none');
      if (isExpanded) {
        this.dateRangeDash.classList.add('d-none');
        this.eventEndDateInput.classList.add('d-none');
        this.eventEndDateInput.value = '';
        this.eventRangeToggleBtn.textContent = '▸';
      } else {
        this.dateRangeDash.classList.remove('d-none');
        this.eventEndDateInput.classList.remove('d-none');
        if (!this.eventEndDateInput.value) this.eventEndDateInput.value = this.eventStartDateInput.value;
        this.eventRangeToggleBtn.textContent = '◂';
      }
    });

    this.saveBtn.addEventListener('click', () => {
      const [index] = this.grid.getSelection();
      if (index < 0) return;
      const color = this.colorInput ? this.colorInput.value : '';
      const description = this.descriptionInput ? this.descriptionInput.value : '';

      const startDateStr = this.eventStartDateInput.value;
      const startDate = dayjs(startDateStr, 'YYYY-MM-DD');
      if (!startDate.isValid())  return showWarning('Invalid Date: ' + startDateStr);

      let endDate = startDate;
      if (!this.eventEndDateInput.classList.contains('d-none')) {
        const endDateStr = this.eventEndDateInput.value;
        endDate = dayjs(endDateStr, 'YYYY-MM-DD');
        if (!endDate.isValid())  return  showWarning('Invalid Date: ' + endDateStr);
      }

      const calendar = this.dataManager.getSelectedCalendarInfo();
      if (calendar.id != this.popupCurrentCalendarId)  throw new Error('Selected calendar changed');
      const birthDate = dayjs(calendar.birthDate, 'YYYY-MM-DD');
      const newIndex = startDate.diff(birthDate, 'day');
      let newLength = Math.max(1, endDate.diff(startDate, 'day') + 1);

      if (newIndex < 0)  return showWarning('Start date is earlier than the calendar start date');
      if (newIndex >= calendar.totalDays)  return showWarning('Start date is later than the calendar end date');
      if (newLength < 1)  return showWarning('End date is earlier than start date');
      if (newIndex + newLength > calendar.totalDays)  newLength = calendar.totalDays - newIndex;

      this.dataManager.changeEvent(index, newIndex, newLength, color, description);
      this.close();
    });

    this.clearBtn.addEventListener('click', () => {
      const [index] = this.grid.getSelection();
      if (index < 0) return;
      this.dataManager.removeEvent(index);
      this.close();
    });

    window.addEventListener('keydown', (e) => {
      if (!this.isOpen()) return;
      if (e.key === 'Enter') {
        this.saveBtn.click();
      }
      if (e.key === 'Escape') {
        this.close();
      }
    });
  }

  public show(calendar: CalendarInfo, index: number, length: number, existingEvent: LifeEvent | null, clientX: number, clientY: number): void {
    const startDate = calendar.birthDate.add(index, 'day');
    const endDate = length == 1 ? null : startDate.add(length - 1, 'day');

    this.eventStartDateInput.classList.remove('d-none');
    this.eventStartDateInput.value = startDate.format('YYYY-MM-DD');
    if (endDate != null) {
      this.dateRangeDash.classList.remove('d-none');
      this.eventEndDateInput.classList.remove('d-none');
      this.eventEndDateInput.value = endDate.format('YYYY-MM-DD');
      this.eventRangeToggleBtn.classList.add('d-none');
      this.eventRangeToggleBtn.textContent = '▸';
    } else {
      this.dateRangeDash.classList.add('d-none');
      this.eventEndDateInput.classList.add('d-none');
      this.eventEndDateInput.value = '';
      this.eventRangeToggleBtn.classList.remove('d-none');
      this.eventRangeToggleBtn.textContent = '▸';
    }

    if (existingEvent) {
      this.colorInput.value = existingEvent.color;
      this.descriptionInput.value = existingEvent.description || '';
      this.clearBtn.style.display = 'block';
    } else {
      this.descriptionInput.value = '';
      this.clearBtn.style.display = 'none';
    }

    const x = clientX;
    const y = clientY;
    this.popup.classList.remove('d-none');
    this.popup.style.left = '0px';
    this.popup.style.top = '0px';
    const w = this.popup.offsetWidth;
    const h = this.popup.offsetHeight;
    const containerRect = this.canvasContainer.getBoundingClientRect();
    let left = x - containerRect.left;
    let top = y - containerRect.top;
    const maxLeft = Math.max(0, containerRect.width - w - 12);
    const maxTop = Math.max(0, containerRect.height - h - 12);
    if (left > maxLeft) left = maxLeft;
    if (top > maxTop) top = maxTop;
    if (left < 0) left = 0;
    if (top < 0) top = 0;
    this.popup.style.left = left + 'px';
    this.popup.style.top = top + 'px';

    this.descriptionInput.focus();

    this.popupCurrentCalendarId = calendar.id;
  }

  public isOpen(): boolean {
    return !this.popup.classList.contains('d-none');
  }

  public close(): void {
    this.popup.classList.add('d-none');
    this.grid.clearSelection();
    this.grid.drawGrid();
  }
} 